import {
  EnvironmentInjector,
  Injectable,
  inject,
  runInInjectionContext,
} from '@angular/core';
import { Auth, signInWithEmailAndPassword, signOut } from '@angular/fire/auth';
import { from, map, Observable } from 'rxjs';

import { LoginRequest } from '@core/interfaces/api/auth.interface';
import { ApiService } from '../api-service/api.service';
import { ApiResponse } from '@core/interfaces/api/api-response.interface';
import { UserInfoMapper } from '@core/mappers/user-info.mapper';
import { UserInfoDto } from '@core/dtos/user-info.dto';
import { UserInfo } from '@core/interfaces/entities/user-info.interface';
import { RESOURCES } from '@shared/constants/resources.constants';
import { EmailVerificationService } from '@core/auth/email-verification/service/email-verification.service';

@Injectable({
  providedIn: 'root',
})
export class AuthFirebaseService {
  private readonly USER_STORAGE_KEY: string = 'user_state';
  private readonly PROFILE_STORAGE_KEY: string = 'profile_state';
  private emailVerificationService: EmailVerificationService = inject(EmailVerificationService);

  constructor(
    private auth: Auth,
    private envInjector: EnvironmentInjector,
    private apiService: ApiService,
    private userInfoMapper: UserInfoMapper
  ) {}

  login(credentials: LoginRequest) {
    return from(
      runInInjectionContext(this.envInjector, () =>
        signInWithEmailAndPassword(
          this.auth,
          credentials.email,
          credentials.password
        )
      )
    );
  }

  private clearAuthData(): void {
    localStorage.removeItem(this.USER_STORAGE_KEY);
    localStorage.removeItem(this.PROFILE_STORAGE_KEY);
    this.emailVerificationService.clearPrompt();
  }

  logout() {
    this.clearAuthData();
    return from(
      runInInjectionContext(this.envInjector, () => signOut(this.auth))
    );
  }

  public getUserInfo(): Observable<ApiResponse<UserInfoDto>> {
    return this.apiService
      .get<ApiResponse<UserInfo>>(RESOURCES.administratorsMe)
      .pipe(
        map((response) => this.userInfoMapper.mapFromApiResponse(response))
      );
  }
}
